import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { GET_PRODUCTS_BY_ID } from 'apollo/queries';
import { productsClient } from 'apollo';
import * as actions from 'containers/Groups/actions';
import { currentBrandSelector, isEnterpriseContextSelector } from 'store/global/selectors';
import QueryRetryWrapper from 'components/QueryRetryWrapper';
import LoadingSpinner from 'components/LoadingSpinner';
import Table from 'react-bootstrap/Table';
import { Alert } from 'react-bootstrap';
import { getUpcFromItem } from 'utils/functions';
import { queryStores } from 'utils/queries';
import './styles.scss';

interface Props {
  clientId: string;
  data: any;
  currentBrand: string;
  isEnterpriseContext: boolean;
  onClose(): void;
}

interface State {
  stores: Array<any>;
  loadingState: number;
}

class ViewGroupDetails extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      stores: [],
      loadingState: 0
    };
  }

  getInfoForItem = (item: any, backendItems: Array<any>) => {
    const backendItem = backendItems.find(backendItem => backendItem.id === item.slice(2));
    if (backendItem) {
      return { firstLabel: backendItem.description, secondLabel: getUpcFromItem(backendItem) };
    }
    return { firstLabel: item };
  };

  filterAndSortItems = items => {
    let result = items.filter(item => item);
    return _.orderBy(result, ['firstLabel']);
  };

  returnOnlyName = (name: string) => {
    const splittedName = name.split(',');

    return splittedName[0];
  };

  createItemFromStore = (store: any) => {
    const storeName = `${store.name !== null ? this.returnOnlyName(store.name) : store.address}`;
    const storeAddress = `${store.address}, ${store.city}, ${store.state} ${store.postalCode}`;
    return { storeName, storeAddress };
  };

  createStoreKeyFromData = store => `${store.nielsenTag}-${store.spinsTag}`;

  queryStores = () => {
    const { clientId, data } = this.props;
    return queryStores(clientId, data.data.stores, storesData => {
      this.setState(previousState => {
        // Do not add stores already present
        let newStores = [...previousState.stores];
        storesData.forEach(newStore => {
          if (
            !previousState.stores.some(
              store => this.createStoreKeyFromData(store) === this.createStoreKeyFromData(newStore)
            )
          ) {
            newStores = newStores.concat([newStore]);
          }
        });
        return {
          stores: _.orderBy(newStores, ['name'])
        };
      });
    });
  };

  renderLoading = () => {
    switch (this.state.loadingState) {
      case 0:
        setTimeout(() => {
          this.setState({ loadingState: 1 });
        }, 3000);
        return <LoadingSpinner />;
      case 1:
        setTimeout(() => {
          this.setState({ loadingState: 2 });
        }, 5000);
        return <LoadingSpinner message="Still processing data." />;
      case 2:
        setTimeout(() => {
          this.setState({ loadingState: 3 });
        }, 10000);
        return <Alert variant="warning">An error occurred trying to fetch the stores.</Alert>;
      default:
        return <Alert variant="warning">An error occurred trying to fetch the stores.</Alert>;
    }
  };

  renderModalBody = () => {
    const { data, clientId, currentBrand, isEnterpriseContext } = this.props;
    const { stores } = this.state;
    return (
      <div className="content">
        <table className="counts">
          <tbody>
            <tr>
              {data.data.accounts ? (
                data.data.accounts.length > 0 ? (
                  <td>Accounts: {data.data.accounts.length}</td>
                ) : null
              ) : null}
              {data.data.items ? (
                data.data.items.length > 0 ? (
                  <td>Items: {data.data.items.length}</td>
                ) : null
              ) : null}
              {data.data.states ? (
                data.data.states.length > 0 ? (
                  <td>States: {data.data.states.length}</td>
                ) : null
              ) : null}
              {data.data.stores ? (
                data.data.stores.length > 0 ? (
                  <td>Stores: {data.data.stores.length}</td>
                ) : null
              ) : null}
            </tr>
          </tbody>
        </table>
        {data.context === 'signals-v2-store-group' ? (
          <div className="table-container">
            {this.queryStores()}
            {data.data.stores.length > 0 ? (
              stores.length === 0 ? (
                this.renderLoading()
              ) : (
                <Table className="details-table" responsive>
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stores.map((item, index) => {
                      return (
                        <tr key={index}>
                          <td>{this.createItemFromStore(item).storeName}</td>
                          <td>{this.createItemFromStore(item).storeAddress}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )
            ) : (
              <Table className="acc-only-details-table" responsive>
                <thead>
                  <tr>
                    <th>Account</th>
                  </tr>
                </thead>
                <tbody>
                  {data &&
                    data.data.accounts.length > 0 &&
                    data.data.accounts
                      .slice()
                      .sort()
                      .map((item, index) => {
                        return (
                          <tr key={index}>
                            <td>{item}</td>
                          </tr>
                        );
                      })}
                </tbody>
              </Table>
            )}
          </div>
        ) : (
          <QueryRetryWrapper
            query={GET_PRODUCTS_BY_ID}
            client={productsClient as any}
            variables={{
              clientId,
              // The service expects the id, which is the UPC without starting '00'
              ids: data.data.items.map(item => item.slice(2)),
              ...(isEnterpriseContext && { brand: currentBrand })
            }}
            fetchPolicy="cache-first"
          >
            {({ data: { batchListProducts } }) => {
              const items = data.data.items.map(item =>
                this.getInfoForItem(item, batchListProducts.items)
              );
              return (
                <div className="table-container">
                  <Table className="details-table" responsive>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>UPC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items &&
                        items.length > 0 &&
                        this.filterAndSortItems(items).map((item, index) => {
                          return item.secondLabel ? (
                            <tr key={index}>
                              <td>{item.firstLabel}</td>
                              <td>{item.secondLabel}</td>
                            </tr>
                          ) : (
                            <tr key={index}>
                              <td />
                              <td>{item.firstLabel}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                </div>
              );
            }}
          </QueryRetryWrapper>
        )}
      </div>
    );
  };

  render() {
    return this.renderModalBody();
  }
}

const mapStateToProps = state => ({
  currentBrand: currentBrandSelector(state),
  isEnterpriseContext: isEnterpriseContextSelector(state)
});

const mapDispatchToProps = dispatch => ({
  onClose: () => {
    dispatch(actions.setShow(false));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ViewGroupDetails);
